import type { ReactNode } from 'react';
import { MSym } from '@storeo/ui';

export const PANEL_TABS = ['Templates', 'Estilo', 'Seções', 'Checkout'] as const;
export type PanelTab = (typeof PANEL_TABS)[number];

const TAB_ICON: Record<PanelTab, string> = {
  Templates: 'auto_awesome',
  Estilo: 'palette',
  Seções: 'view_agenda',
  Checkout: 'credit_card',
};

/** Os 6 modelos, numerados como no protótipo. `key` = valor de `store.template`
 *  (registro autoritativo em packages/templates/src/index.ts → LUMINA_TEMPLATES). */
const TEMPLATES: {
  key: string;
  n: number;
  name: string;
  tag: string;
  tagCls: string;
  hoverCls: string;
  desc: string;
}[] = [
  {
    key: 'lumina-cinema',
    n: 1,
    name: 'Lumina Cinema',
    tag: 'Ativo Agora',
    tagCls: 'bg-primary text-on-primary font-bold uppercase tracking-wider',
    hoverCls: 'group-hover:text-primary',
    desc: 'Estética dark cinematográfica, hero imersivo, ideal para moda high-ticket e tecnologia de luxo.',
  },
  {
    key: 'cyber-noir',
    n: 2,
    name: 'Cyber Noir',
    tag: 'Infoprodutos & Dev',
    tagCls: 'text-tertiary bg-tertiary/10 font-mono',
    hoverCls: 'group-hover:text-tertiary',
    desc: 'Minimalismo escuro com luzes ciano e micro-bordas para SaaS, automações e softwares.',
  },
  {
    key: 'obsidian-luxury',
    n: 3,
    name: 'Obsidian Luxury',
    tag: 'Jóias & Premium',
    tagCls: 'text-amber-300 bg-amber-400/10',
    hoverCls: 'group-hover:text-amber-300',
    desc: 'Tipografia serifada sobre ônix puro com toques dourados champagne e atmosfera de gala.',
  },
  {
    key: 'portal-vsl',
    n: 4,
    name: 'Portal Notícia VSL',
    tag: 'Urgência Máxima',
    tagCls: 'text-error bg-error/15 font-bold',
    hoverCls: 'group-hover:text-error',
    desc: 'Estilo manchete investigativa com tarja vermelha urgente, quebra imediata de ceticismo e player VSL central.',
  },
  {
    key: 'clean-offer',
    n: 5,
    name: 'Clean Direct Offer',
    tag: 'Lead Magnet',
    tagCls: 'text-secondary bg-secondary/15',
    hoverCls: 'group-hover:text-secondary',
    desc: 'Hiperfoco em conversão rápida: cronômetro regressivo no topo, depoimentos em carrossel e CTA em 1 clique.',
  },
  {
    key: 'fast-grid',
    n: 6,
    name: 'Fast-Commerce Grid',
    tag: 'E-commerce 3x',
    tagCls: 'text-primary bg-primary/10',
    hoverCls: 'group-hover:text-primary',
    desc: 'Catálogo ultra veloz com order bump visual, frete grátis instantâneo no card e checkout na página inicial.',
  },
];

const SWATCHES: { label: string; value: string; hex: string }[] = [
  { label: 'Acento Primário', value: '#6366F1', hex: '#6366F1' },
  { label: 'Botão de Compra', value: '#10B981', hex: '#10B981' },
  { label: 'Fundo Geral', value: '#0A0E17', hex: '#0A0E17' },
  { label: 'Vidro & Cards', value: 'Glass 40%', hex: '#1C1F29' },
];

const BLOCKS = [
  '1. Topbar de Urgência & PIX',
  '2. Hero Banner com 1-Clique',
  '3. Vitrine de Produtos & Order Bump',
  '4. Prova Social & Reviews Vivos',
  '5. Checkout Transparente (Sem Redir)',
];

function AccordionShell({
  icon,
  title,
  aside,
  children,
}: {
  icon: string;
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="bg-surface-container p-space-sm rounded-xl flex flex-col gap-space-xs shadow-sm">
      <div className="flex items-center justify-between">
        <span className="font-label-md text-label-md font-bold text-white flex items-center gap-1.5">
          <MSym name={icon} className="text-[16px] text-primary" />
          {title}
        </span>
        {aside}
      </div>
      {children}
    </div>
  );
}

/** Painel lateral low-code — réplica de docs/prototype/lpplat/construtor/code.html
 *  (linhas ~82–288): abas + 6 templates numerados + acordeões permanentes. Os acordeões
 *  (Paleta / Tipografia / Blocos / Identidade) são visuais nesta entrega, como no protótipo.
 *  O seletor de template é funcional (dirige o canvas). Estilo/Seções/Checkout recebem os
 *  controles funcionais atuais via slot, pra não regredir função. */
export function StudioPanel({
  tab,
  onTab,
  template,
  onTemplate,
  estiloSlot,
  secoesSlot,
  checkoutSlot,
  open,
}: {
  tab: PanelTab;
  onTab: (t: PanelTab) => void;
  template: string;
  onTemplate: (key: string) => void;
  estiloSlot: ReactNode;
  secoesSlot: ReactNode;
  checkoutSlot: ReactNode;
  /** Controlado pelo hambúrguer da barra de controle. Recolhido = fora do fluxo (canvas ganha a largura). */
  open: boolean;
}) {
  if (!open) return null;

  return (
    <div
      className="w-full lg:w-[410px] shrink-0 bg-surface-container-lowest p-space-md rounded-2xl flex flex-col gap-space-md shadow-xl overflow-y-auto"
      style={{ maxHeight: '80vh' }}
    >
      {/* Abas */}
      <div className="grid grid-cols-4 gap-1 p-1 bg-surface-container-low rounded-lg">
        {PANEL_TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onTab(t)}
            className={`py-2 px-1 text-center font-label-sm text-label-sm rounded-md transition-all flex flex-col items-center gap-0.5 ${
              tab === t
                ? 'bg-primary text-on-primary font-bold shadow-sm'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <MSym name={TAB_ICON[t]} className="text-[18px]" />
            <span>{t}</span>
          </button>
        ))}
      </div>

      {/* Conteúdo da aba */}
      {tab === 'Templates' ? (
        <div className="flex flex-col gap-space-sm">
          <div className="flex items-center justify-between">
            <span className="font-label-md text-label-md uppercase tracking-wider text-on-surface-variant font-bold">
              6 Modelos de Alta Conversão
            </span>
            <span className="font-label-sm text-label-sm text-secondary bg-secondary/10 px-2 py-0.5 rounded-full">
              Otimizados p/ Vendas
            </span>
          </div>
          <div className="grid grid-cols-1 gap-space-xs">
            {TEMPLATES.map((t) => {
              const active = template === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => onTemplate(t.key)}
                  className={`group p-space-sm rounded-xl text-left transition-all ${
                    active
                      ? 'bg-surface-container-high shadow-md'
                      : 'bg-surface-container hover:bg-surface-container-high shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {active ? (
                        <span className="w-2.5 h-2.5 rounded-full bg-secondary animate-ping" />
                      ) : null}
                      <span
                        className={`font-headline-sm text-[0.95rem] font-bold text-white ${active ? '' : t.hoverCls} transition-colors`}
                      >
                        {t.n}. {t.name}
                      </span>
                    </div>
                    <span
                      className={`font-label-sm text-[10px] px-2 py-0.5 rounded-full ${
                        active ? t.tagCls : `${t.tagCls} rounded-md`
                      }`}
                    >
                      {active ? 'Ativo Agora' : t.tag}
                    </span>
                  </div>
                  <p className="font-body-sm text-body-sm text-on-surface-variant mt-1.5">
                    {t.desc}
                  </p>
                  {active ? (
                    <div className="mt-2.5 flex items-center gap-2 text-primary font-label-sm text-label-sm">
                      <MSym name="visibility" className="text-[16px]" />
                      <span>Visualizando no Canvas</span>
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {tab === 'Estilo' ? estiloSlot : null}
      {tab === 'Seções' ? secoesSlot : null}
      {tab === 'Checkout' ? checkoutSlot : null}

      {/* Acordeões permanentes (visuais) */}
      <div className="flex flex-col gap-space-sm pt-space-xs">
        <AccordionShell
          icon="colorize"
          title="Paleta de Cores da Loja"
          aside={
            <span className="font-label-sm text-label-sm text-on-surface-variant font-mono">
              Hex Direto
            </span>
          }
        >
          <div className="grid grid-cols-2 gap-2 mt-1">
            {SWATCHES.map((s) => (
              <div key={s.label} className="flex flex-col gap-1">
                <label className="font-label-sm text-label-sm text-on-surface-variant">
                  {s.label}
                </label>
                <div className="flex items-center gap-2 bg-surface-container-lowest px-2 py-1.5 rounded-lg shadow-inner">
                  <span className="w-5 h-5 rounded-full shadow-sm" style={{ background: s.hex }} />
                  <span className="font-mono text-label-sm text-white font-bold">{s.value}</span>
                </div>
              </div>
            ))}
          </div>
        </AccordionShell>

        <AccordionShell icon="text_fields" title="Tipografia White-Label">
          <div className="flex items-center justify-between bg-surface-container-lowest p-2 rounded-lg mt-1">
            <span className="font-headline-sm text-headline-sm text-white">Plus Jakarta Sans</span>
            <span className="font-label-sm text-label-sm text-primary font-bold">
              Padrão Storeo
            </span>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="flex-1 py-1 rounded bg-surface-container-high text-on-surface font-label-sm text-label-sm text-center hover:bg-primary/20"
            >
              Inter Clean
            </button>
            <button
              type="button"
              className="flex-1 py-1 rounded bg-surface-container-high text-on-surface font-label-sm text-label-sm text-center hover:bg-primary/20"
            >
              Playfair Editorial
            </button>
          </div>
        </AccordionShell>

        <AccordionShell
          icon="drag_indicator"
          title="Blocos da Loja Ativos"
          aside={<span className="font-label-sm text-label-sm text-secondary">5 visíveis</span>}
        >
          <div className="flex flex-col gap-1.5 mt-1">
            {BLOCKS.map((b) => (
              <div
                key={b}
                className="flex items-center justify-between p-2 rounded-lg bg-surface-container-lowest"
              >
                <div className="flex items-center gap-2">
                  <MSym name="drag_handle" className="text-on-surface-variant text-[16px]" />
                  <span className="font-label-sm text-label-sm text-white font-semibold">{b}</span>
                </div>
                <MSym name="toggle_on" className="text-secondary text-[18px]" />
              </div>
            ))}
          </div>
        </AccordionShell>

        <AccordionShell icon="image" title="Identidade do Cliente">
          <div className="grid grid-cols-2 gap-2">
            <div className="p-2.5 rounded-lg bg-surface-container-lowest text-center flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-surface-container-high transition-colors">
              <MSym name="add_photo_alternate" className="text-on-surface-variant text-[20px]" />
              <span className="font-label-sm text-[11px] text-white">Logo Lumina (PNG)</span>
            </div>
            <div className="p-2.5 rounded-lg bg-surface-container-lowest text-center flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-surface-container-high transition-colors">
              <MSym name="cloud_download" className="text-on-surface-variant text-[20px]" />
              <span className="font-label-sm text-[11px] text-white">Favicon Custom</span>
            </div>
          </div>
        </AccordionShell>
      </div>
    </div>
  );
}
